import useInertiaData from '@/hooks/useInertiaData';
import useThemeColors from '@/hooks/useThemeColors';
import { Academy } from '@/types';
import { Popover } from '@headlessui/react';
import { useForm } from '@inertiajs/react';
import { ChevronDown } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';

const PlatformSelector: React.FC = () => {
    const { user } = useInertiaData();
    const colors = useThemeColors();

    const academies: Academy[] = useMemo(() => {
        return user?.academies ?? [];
    }, [user]);

    const currentAcademy: Academy = useMemo(() => {
        return user?.current_academy as Academy;
    }, [user]);

    const { post, setData, data } = useForm({
        academy_id: 0,
    });

    const logoClasses =
        'h-7 w-auto max-w-[140px] object-contain sm:h-8 sm:max-w-[180px] lg:h-6 lg:max-w-none';

    useEffect(() => {
        if (data.academy_id) {
            post('/auth/select-academy/', {
                onSuccess: () => {
                    window.location.href = '/';
                },
            });
        }
    }, [data.academy_id]);

    // If only one academy, show logo without dropdown
    if (academies.length <= 1) {
        return (
            <div className="flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2">
                <img
                    src={currentAcademy.logo_url}
                    alt={currentAcademy.name}
                    className={logoClasses}
                />
            </div>
        );
    }

    return (
        <Popover className="relative">
            {({ open }) => (
                <>
                    <Popover.Button className={`flex items-center space-x-2 rounded-lg border border-gray-200 bg-white px-3 py-2 transition-colors duration-200 hover:bg-gray-50 focus:ring-2 focus:outline-none ${colors.buttonPrimaryFocus}`}>
                        <img
                            src={currentAcademy.logo_url}
                            alt={currentAcademy.name}
                            className={logoClasses}
                        />
                        <ChevronDown
                            size={16}
                            className={`text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                        />
                    </Popover.Button>

                    <Popover.Panel className="absolute top-full left-0 z-20 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-xl">
                        <div className="py-1">
                            {academies.map((platform) => (
                                <button
                                    key={platform.id}
                                    onClick={() => {
                                        setData('academy_id', platform.id);
                                    }}
                                    className="flex w-full items-center space-x-3 p-3 text-left text-gray-900 transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                                >
                                    <img
                                        src={platform.logo_url}
                                        alt={platform.name}
                                        className="h-5 w-auto"
                                    />
                                </button>
                            ))}
                        </div>
                    </Popover.Panel>
                </>
            )}
        </Popover>
    );
};

export default PlatformSelector;
